import './CustomerFeedbackPopup.css';
import { tIssue, eIssueStatus } from '../data/Issues';

export type tEpicRecord = {
  epicName: string;
  totalIssues: number;
  backlog: number;
  inProgress: number;
  awaitingRelease: number;
  released: number;
};

// Helper function to determine row background color based on release status
const getRowBackgroundColor = (record: tEpicRecord, isLocked: boolean): string => {
  // Priority 0: Grey if the epic is locked
  if (isLocked) {
    return 'rgba(128, 128, 128, 0.15)'; // Grey, faded
  }
  
  // Priority 1: Amber if there are stories awaiting release
  if (record.awaitingRelease > 0) {
    return 'rgba(255, 191, 0, 0.2)'; // Amber/orange, faded
  }
  
  // Priority 2: Red if 0 stories released
  if (record.released === 0) {
    return 'rgba(255, 0, 0, 0.15)'; // Red, faded
  }
  
  // Priority 3: Green if exactly 10 stories released
  if (record.released === 10) {
    return 'rgba(0, 255, 0, 0.2)'; // Green, faded
  }
  
  // Priority 4: Yellow if more than 0 but not 10 stories released
  if (record.released > 0 && record.released !== 10) {
    return 'rgba(255, 255, 0, 0.2)'; // Yellow, faded
  }
  
  // Default: no highlight
  return 'transparent';
};

interface EpicReportPopupProps {
  issues: tIssue[];
  sprint: number;
  mobileGame: boolean;
  closeEpicReportCallback: () => void;
}

export default function EpicReportPopup(props: EpicReportPopupProps) {
  // Get unique epics (excluding "Misc")
  const uniqueEpics = Array.from(new Set(props.issues.filter(issue => issue.epic !== 'Misc').map((issue) => issue.epic)))
    .sort();

  // Calculate epic statistics
  const calculateEpicStats = (epicName: string): tEpicRecord => {
    // Get ALL issues in the epic (not just available ones) for total count
    const allEpicIssues = props.issues.filter(
      (issue) => issue.epic === epicName
    );
    
    // Get only available issues for status calculations
    const availableEpicIssues = props.issues.filter(
      (issue) => issue.epic === epicName && issue.available <= props.sprint
    );

    const totalIssues = allEpicIssues.length;
    
    // Backlog: issues that are Open
    const backlog = availableEpicIssues.filter(
      (issue) => issue.status === eIssueStatus.Open
    ).length;

    // In Progress: issues in Todo, InProgress, InQA, or Done (but not yet released)
    const inProgress = availableEpicIssues.filter(
      (issue) => 
        issue.status === eIssueStatus.Todo || 
        issue.status === eIssueStatus.InProgress || 
        issue.status === eIssueStatus.InQA || 
        issue.status === eIssueStatus.Done
    ).length;

    // Awaiting Release: issues that are Closed but not yet released
    const awaitingRelease = availableEpicIssues.filter(
      (issue) => issue.status === eIssueStatus.Closed && !issue.released
    ).length;

    // Released: issues that are released
    const released = availableEpicIssues.filter(
      (issue) => issue.released === true
    ).length;

    return {
      epicName,
      totalIssues,
      backlog,
      inProgress,
      awaitingRelease,
      released,
    };
  };

  const epicRecords = uniqueEpics.map(calculateEpicStats);

  return (
    <div className="popup-overlay">
      <div className={`customer-feedback-popup-modal`}>
        <h3>🏆 Epics Report</h3>
        <table className="feedback-table">
          <thead>
            <tr>
              <th>Epic Name</th>
              <th>Total Issues</th>
              <th>Available in Backlog</th>
              <th>In Progress</th>
              <th>Awaiting Release</th>
              <th>Released</th>
            </tr>
          </thead>
          <tbody>
            {epicRecords.map((record, index) => {
              const isMobileGame = record.epicName === "Mobile Game";
              const isLocked = isMobileGame && !props.mobileGame;
              const backgroundColor = getRowBackgroundColor(record, isLocked);
              
              return (
                <tr key={index} style={{ backgroundColor }}>
                  <td>{isLocked ? '🔒 Locked' : record.epicName}</td>
                  <td style={{ textAlign: 'center' }}>
                    {isLocked ? '🔒 Locked' : record.totalIssues}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {isLocked ? '🔒 Locked' : record.backlog}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {isLocked ? '🔒 Locked' : record.inProgress}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {isLocked ? '🔒 Locked' : record.awaitingRelease}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {isLocked ? '🔒 Locked' : record.released}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        <button className="close-button" onClick={props.closeEpicReportCallback}>Close</button>
      </div>
    </div>
  );
}
